// The species by week report (Brunswick County's template): one row per
// species, one column per week, each week headed by its number in the year
// and its dates - "Week 41" over "Oct 5 – Oct 11, 2026".
//
// The latest week comes first. Weeks start on the organization's day (the
// pivot's meta.weekStart). Week 1 of a year is the week holding January 1,
// so in a Monday year it can begin in late December.
import { weekSpan } from "./WeekPicker";

const DAY_MS = 86400000;
const parse = (iso) => new Date(`${iso}T00:00:00Z`);

const weekStartOf = (date, weekStart) => {
  const first = weekStart === "sunday" ? 0 : 1;
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() - first + 7) % 7));
  return d;
};

/** The week's number in the year its last day falls in. */
export const weekNumber = (iso, weekStart = "monday") => {
  const start = parse(iso);
  const year = new Date(start.getTime() + 6 * DAY_MS).getUTCFullYear();
  const first = weekStartOf(new Date(Date.UTC(year, 0, 1)), weekStart);
  return Math.round((start - first) / (7 * DAY_MS)) + 1;
};

// The week axis newest first: the entries, their counts and their totals.
const latestFirst = (model, weeksAreColumns) => {
  const count = weeksAreColumns ? model.columns.length : model.rows.length;
  const flip = (i) => count - 1 - i;
  const values = new Map();
  model.values.forEach((value, key) => {
    const [r, c] = key.split(":").map(Number);
    values.set(weeksAreColumns ? `${r}:${flip(c)}` : `${flip(r)}:${c}`, value);
  });
  return weeksAreColumns
    ? { ...model, values, columns: [...model.columns].reverse(), columnTotals: [...model.columnTotals].reverse() }
    : { ...model, values, rows: [...model.rows].reverse(), rowTotals: [...model.rowTotals].reverse() };
};

export const speciesByWeek = (model) => {
  const weekStart = model.meta?.weekStart ?? "monday";
  const label = (entry) => {
    const iso = entry.key[0];
    const lines = [`Week ${weekNumber(iso, weekStart)}`, weekSpan(iso)];
    return { ...entry, display: { lines, text: `${lines[0]} (${lines[1]})` } };
  };
  // the columns are weeks; once transposed, the rows are
  const weeksAreColumns = model.colDims?.[0] === "week";
  // its cells say "Week 41 (...)", so the dimension is just "Week"
  const dimensionLabels = { ...model.dimensionLabels, week: "Week" };
  // the latest week first, as people read the season back from today
  const latest = latestFirst(model, weeksAreColumns);
  return weeksAreColumns
    ? { ...latest, dimensionLabels, columns: latest.columns.map(label) }
    : { ...latest, dimensionLabels, rows: latest.rows.map(label) };
};
